import { useEffect, useState, useMemo } from "react";
import api, { inr, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  MapPin, Printer, ArrowRight, ArrowLeft, Loader2, Search,
  Truck, PackageCheck, CheckCircle2, BadgeCheck, ShieldCheck,
} from "lucide-react";

const MACHINES = [
  { key: "sla", label: "SLA (Formlabs)" },
  { key: "dlp", label: "DLP (Asiga/EnvisionTEC)" },
  { key: "msla", label: "MSLA (Phrozen/Elegoo)" },
  { key: "wax_jet", label: "Wax-Jet / MJP" },
  { key: "fdm", label: "FDM (prototypes)" },
];
const MACHINE_LABEL = Object.fromEntries(MACHINES.map((m) => [m.key, m.label.split(" ")[0]]));

/** PrintToRealityWizard — 3-step buyer flow to send a purchased CAD to a
 *  CAM Vendor. Consumes the buyer's order + optional cam_weight to compute
 *  an approximate cost per vendor's rate/gram (buyer sees clear "final cost
 *  confirmed by vendor" language). */
export default function PrintToRealityWizard({ order, open, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [city, setCity] = useState("");
  const [cityQuery, setCityQuery] = useState("");
  const [cities, setCities] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [machineFilter, setMachineFilter] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [deliveryMode, setDeliveryMode] = useState("pickup");
  const [address, setAddress] = useState("");
  const [issuing, setIssuing] = useState(false);

  // Load cities once
  useEffect(() => {
    if (!open) return;
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
  }, [open]);

  // Fetch vendors when city changes (or machine changes)
  useEffect(() => {
    if (!city) return;
    setVendorsLoading(true);
    setSelectedVendor(null);
    const params = { city };
    if (machineFilter) params.machine = machineFilter;
    api.get("/vendors", { params })
      .then((r) => setVendors(r.data))
      .catch(() => setVendors([]))
      .finally(() => setVendorsLoading(false));
  }, [city, machineFilter]);

  const filteredCities = useMemo(() => {
    const q = cityQuery.trim().toLowerCase();
    if (!q) return cities.slice(0, 40);
    return cities.filter((c) => c.toLowerCase().includes(q)).slice(0, 40);
  }, [cities, cityQuery]);

  // Reset when reopened
  useEffect(() => {
    if (!open) return;
    setStep(1); setCity(""); setCityQuery(""); setVendors([]);
    setMachineFilter(""); setSelectedVendor(null); setDeliveryMode("pickup"); setAddress("");
  }, [open]);

  const estimateFor = (v) => {
    const w = order?.cam_weight;
    if (!v?.rate_per_gram || !w) return null;
    return Math.round(v.rate_per_gram * w);
  };

  const issue = async () => {
    if (!selectedVendor || !deliveryMode) return;
    if (deliveryMode === "delivery" && !address.trim()) {
      return toast.error("Add a delivery address, or switch to pickup.");
    }
    setIssuing(true);
    try {
      const { data } = await api.post("/print-jobs", {
        order_id: order.id,
        vendor_id: selectedVendor.id,
        machine: machineFilter || undefined,
        delivery_mode: deliveryMode,
        delivery_address: deliveryMode === "delivery" ? address.trim() : undefined,
      });
      if (data.duplicate) toast.info("You already have an open job with this vendor — jumping to it.");
      else toast.success(`Job sent to ${selectedVendor.shop_name}. They'll respond shortly.`);
      onCreated?.(data.job_id);
      onClose();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-primary" /> Print to Reality
            <span className="text-xs font-mono font-normal uppercase text-muted-foreground ml-2">Step {step} of 3</span>
          </DialogTitle>
        </DialogHeader>

        {/* Step 1 — City picker */}
        {step === 1 && (
          <div data-testid="ptr-step-city">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Which city are you in? We'll show verified 3D print vendors nearest to you.
            </p>
            <div className="mt-4 relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={cityQuery}
                onChange={(e) => setCityQuery(e.target.value)}
                placeholder="Type your city — Mumbai, Delhi, Surat…"
                data-testid="ptr-city-search"
                className="w-full border border-border rounded-md pl-9 p-2.5 text-sm bg-background"
                autoFocus
              />
            </div>
            <div className="mt-3 max-h-64 overflow-y-auto rounded-md border border-border bg-secondary/30" data-testid="ptr-city-list">
              {filteredCities.length === 0 ? (
                <p className="text-xs text-muted-foreground p-4 text-center">No cities match — check spelling.</p>
              ) : (
                filteredCities.map((c) => (
                  <button
                    key={c}
                    onClick={() => { setCity(c); setStep(2); }}
                    data-testid={`ptr-city-${c}`}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left text-sm hover:bg-secondary border-b border-border last:border-0 ${city === c ? "bg-primary/10 text-primary" : ""}`}
                  >
                    <span className="inline-flex items-center gap-2"><MapPin className="h-3.5 w-3.5 text-muted-foreground" /> {c}</span>
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Step 2 — Vendor list */}
        {step === 2 && (
          <div data-testid="ptr-step-vendor">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm text-muted-foreground">
                Vendors in <strong className="text-foreground">{city}</strong>
              </p>
              <button onClick={() => setStep(1)} className="text-xs text-primary hover:underline" data-testid="ptr-change-city">change</button>
            </div>

            <div className="mt-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Filter by machine (optional)</p>
              <div className="flex flex-wrap gap-1.5">
                <button
                  onClick={() => setMachineFilter("")}
                  data-testid="ptr-machine-any"
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!machineFilter ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                >Any</button>
                {MACHINES.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setMachineFilter(m.key)}
                    data-testid={`ptr-machine-${m.key}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${machineFilter === m.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
                  >{m.label.split(" ")[0]}</button>
                ))}
              </div>
            </div>

            <div className="mt-4 space-y-2 max-h-72 overflow-y-auto pr-1">
              {vendorsLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-xs">
                  <Loader2 className="h-3 w-3 animate-spin mr-2" /> Finding vendors near {city}…
                </div>
              ) : vendors.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-8" data-testid="ptr-vendors-empty">
                  No vendors match right now. Our team is onboarding shops in {city} — check back soon.
                </p>
              ) : vendors.map((v) => {
                const est = estimateFor(v);
                const isSel = selectedVendor?.id === v.id;
                return (
                  <button
                    key={v.id}
                    onClick={() => setSelectedVendor(v)}
                    data-testid={`ptr-vendor-${v.id}`}
                    className={`w-full text-left rounded-md border p-3 transition-colors ${isSel ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 shrink-0 rounded-md bg-primary/10 text-primary flex items-center justify-center">
                        <Printer className="h-5 w-5" strokeWidth={1.6} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-sm truncate">{v.shop_name}</p>
                          {v.same_city && <span className="text-[9px] font-mono uppercase tracking-widest bg-emerald-500/15 text-emerald-600 border border-emerald-500/30 rounded-full px-1.5">Same city</span>}
                          {isSel && <CheckCircle2 className="h-4 w-4 text-primary shrink-0 ml-auto" />}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-1">
                          <MapPin className="h-3 w-3" /> {v.city || "—"}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(v.machines || []).map((m) => (
                            <span key={m} className="text-[9px] font-mono uppercase tracking-widest bg-secondary border border-border rounded-full px-1.5">{MACHINE_LABEL[m] || m.toUpperCase()}</span>
                          ))}
                        </div>
                        <div className="mt-2 flex items-center gap-4 text-xs">
                          {v.rate_per_gram ? (
                            <span><span className="text-muted-foreground">Rate</span> <strong className="ml-1">₹{v.rate_per_gram}/g</strong></span>
                          ) : (
                            <span className="text-muted-foreground">Rate set on job</span>
                          )}
                          {est != null ? (
                            <span><span className="text-muted-foreground">Approx.</span> <strong className="ml-1">{inr(est)}</strong></span>
                          ) : (
                            <span className="text-[11px] text-muted-foreground italic">Cost confirmed by vendor after review</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3 — Pickup / Delivery */}
        {step === 3 && selectedVendor && (
          <div data-testid="ptr-step-delivery">
            <p className="text-sm text-muted-foreground">
              How would you like to receive your printed part from <strong className="text-foreground">{selectedVendor.shop_name}</strong>?
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <button
                onClick={() => setDeliveryMode("pickup")}
                data-testid="ptr-mode-pickup"
                className={`rounded-md border p-4 text-left transition-colors ${deliveryMode === "pickup" ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
              >
                <PackageCheck className="h-5 w-5 text-primary mb-2" />
                <p className="font-semibold text-sm">Pickup</p>
                <p className="text-[11px] text-muted-foreground mt-1">Collect from the vendor's shop</p>
              </button>
              <button
                onClick={() => setDeliveryMode("delivery")}
                data-testid="ptr-mode-delivery"
                className={`rounded-md border p-4 text-left transition-colors ${deliveryMode === "delivery" ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}
              >
                <Truck className="h-5 w-5 text-primary mb-2" />
                <p className="font-semibold text-sm">Delivery</p>
                <p className="text-[11px] text-muted-foreground mt-1">Vendor ships to your address</p>
              </button>
            </div>

            {deliveryMode === "delivery" && (
              <div className="mt-4">
                <label className="text-xs font-mono uppercase text-muted-foreground">Delivery address</label>
                <textarea
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={3}
                  data-testid="ptr-address"
                  placeholder="Building, street, city, PIN code"
                  className="mt-1 w-full border border-border rounded-md p-2.5 text-sm bg-background"
                />
              </div>
            )}

            <div className="mt-5 rounded-md border border-border bg-secondary/40 p-3">
              <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">What happens next</p>
              <ul className="text-xs space-y-1 text-foreground leading-relaxed">
                <li>1. Vendor accepts and enters the actual printing cost.</li>
                <li>2. You confirm the quote → payment goes into escrow (charging via Razorpay coming in the next release).</li>
                <li>3. Vendor prints and updates status until your piece is in your hand.</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1.5 border border-border rounded-md px-4 py-2 text-sm" data-testid="ptr-back"><ArrowLeft className="h-3.5 w-3.5" /> Back</button>}
          {step === 2 && selectedVendor && (
            <button onClick={() => setStep(3)} className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5" data-testid="ptr-continue-to-delivery">Continue <ArrowRight className="h-3.5 w-3.5" /></button>
          )}
          {step === 3 && (
            <button onClick={issue} disabled={issuing || (deliveryMode === "delivery" && !address.trim())} className="bg-primary text-primary-foreground rounded-md px-4 py-2 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="ptr-issue-job">
              {issuing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />} Issue print job
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
